/**
 * Leedoc markdown marker definitions
 *
 * Defines custom markers that extend standard markdown with leedoc-specific features.
 * Each marker specifies how it should be handled for preview vs export.
 */

export interface MarkerDefinition {
  /** Unique identifier for this marker */
  name: string;
  /** Regex pattern to match the marker in markdown source */
  pattern: RegExp;
  /** Description of what this marker does */
  description: string;
  /** How to handle for export (return replacement string, or null to strip) */
  cleanForExport: (match: string, ...groups: string[]) => string | null;
  /** Whether this marker appears in preview (true) or is stripped (false) */
  appearsInPreview: boolean;
  /** Whether this marker appears in exported markdown */
  appearsInExport: boolean;
}

/**
 * Registry of all leedoc markdown markers
 */
export const markers: MarkerDefinition[] = [
  {
    name: 'tangle',
    pattern: /<!--\s*tangle:\s*[^>]+\s*-->/g,
    description: 'Script extraction marker - specifies which script file a code block belongs to',
    cleanForExport: () => null, // Strip entirely
    appearsInPreview: false, // Hidden in rendered preview
    appearsInExport: false,
  },
  {
    name: 'toc',
    pattern: /^\s*\[ld:toc(?::(\d)(?:-(\d))?)?\]\s*$/gm,
    description: 'Table of contents - generates navigation from headings. Syntax: [ld:toc], [ld:toc:3], [ld:toc:2-4]',
    cleanForExport: () => null, // Strip entirely
    appearsInPreview: true, // Rendered as clickable navigation
    appearsInExport: false,
  },
  {
    name: 'embed',
    pattern: /<!--\s*embed:\s*[^>]+\s*-->/g,
    description: 'Component embed directive - includes content from another file',
    cleanForExport: () => null, // Already processed by weave, marker stripped
    appearsInPreview: false, // Replaced with embedded content
    appearsInExport: false,
  },
  {
    name: 'vars-frontmatter',
    pattern: /^<!--\s*vars\n[\s\S]*?\n-->\n?/m,
    description: 'Variable definitions block - defines variables for substitution',
    cleanForExport: () => null, // Already processed by weave
    appearsInPreview: false, // Stripped, values shown in form
    appearsInExport: false,
  },
  {
    name: 'collapse',
    pattern: /<!--\s*collapse\s*(?::\s*([^|>]*?))?\s*(?:\|\s*(\d+)\s*)?-->/g,
    description: 'Collapsible code block - shows a truncated preview with expand toggle. Syntax: <!-- collapse: TITLE | LINES -->',
    cleanForExport: () => null, // Strip entirely
    appearsInPreview: false, // Processed by enricher
    appearsInExport: false,
  },
  {
    name: 'media-url',
    // Matches ![alt](media://filename) with optional sizing =WIDTHxHEIGHT
    pattern: /!\[([^\]]*)\]\(media:\/\/([^)\s]+)(?:\s*=(\d*)x(\d*))?\)/g,
    description: 'Media URL - references uploaded images via media:// protocol',
    cleanForExport: (_match, alt, filename, width, height) => {
      // Rewrite to relative path for exported markdown
      // Images will be placed in _images/ folder alongside the doc
      if (width || height) {
        // Use HTML for sized images
        const attrs: string[] = [`src="./_images/${filename}"`];
        if (alt) attrs.push(`alt="${alt.replace(/"/g, '&quot;')}"`);
        if (width) attrs.push(`width="${width}"`);
        if (height) attrs.push(`height="${height}"`);
        return `<img ${attrs.join(' ')}>`;
      }
      return `![${alt}](./_images/${filename})`;
    },
    appearsInPreview: true, // Rendered as image
    appearsInExport: false, // Transformed to relative path (processed by cleanForExport)
  },
];

/**
 * Get a marker definition by name
 */
export function getMarker(name: string): MarkerDefinition | undefined {
  return markers.find(m => m.name === name);
}

/**
 * Extract all media:// references from content
 * Returns array of unique filenames referenced
 */
export function extractMediaReferences(content: string): string[] {
  const mediaMarker = getMarker('media-url');
  if (!mediaMarker) return [];

  const filenames = new Set<string>();
  const pattern = new RegExp(mediaMarker.pattern.source, 'g');

  let match;
  while ((match = pattern.exec(content)) !== null) {
    // match[2] is the filename capture group
    if (match[2]) {
      filenames.add(match[2]);
    }
  }

  return Array.from(filenames);
}

/**
 * Get all markers that should be stripped for export
 */
export function getExportCleanupMarkers(): MarkerDefinition[] {
  return markers.filter(m => !m.appearsInExport);
}
